import React from "react";
import { Bar } from "react-chartjs-2";

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center text-gray-200">
      <i className="fa-solid fa-chart-simple text-lg lg:text-2xl mb-3 opacity-30" />
      <p className="text-xs text-gray-200 leading-relaxed max-w-xs">
        {message}
      </p>
    </div>
  );
}

// Extracted from GrowthLoopHome.jsx unchanged, aside from becoming a
// standalone component that receives its data via props instead of
// closing over Home's local state directly.
const GLCrossViewDashboard = ({
  lang,
  DOMAINS,
  ROLES,
  domainStats,
  consistencyIndex,
  hasAnyResponse,
  responses,
  darkChartOptions,
}) => {
  const roleColors = [
    "rgba(34,211,238,0.8)", // parent   → cyan
    "rgba(251,191,36,0.8)", // educator → amber
    "rgba(52,211,153,0.8)", // doctor   → emerald
    "rgba(168,85,247,0.8)", // observer → purple
  ];

  const chartData = {
    labels: DOMAINS.map((d) => d.title[lang]),
    datasets: ROLES.map((role, idx) => ({
      label: role.charAt(0).toUpperCase() + role.slice(1),
      data: DOMAINS.map((d) => {
        const { total, max } = domainStats(role, d);
        return max ? Math.round((total / max) * 100) : 0;
      }),
      backgroundColor: roleColors[idx],
      borderRadius: 4,
    })),
  };

  const hasData = ROLES.some(hasAnyResponse);

  return (
    <div className="flex justify-center py-2">
      <div className="w-full max-w-2xl space-y-4 text-white">
        <div className="text-center">
          <h2 className="text-base font-bold text-cyan-300 uppercase tracking-wide">
            {lang === "hi" ? "तुलनात्मक दृश्य" : "Cross-View Dashboard"}
          </h2>
          <p className="text-xs text-gray-200 mt-0.5 max-w-xs mx-auto">
            {lang === "hi"
              ? "घर, स्कूल, क्लिनिक और समुदाय — सभी के अवलोकन की तुलना।"
              : "Comparative signals across home, school, clinic and community."}
          </p>
        </div>

        <div className="bg-gray-950 border border-gray-800 rounded-xl p-3">
          {!hasData ? (
            <EmptyState
              message={
                lang === "hi"
                  ? "कम से कम एक आकलन पूरा करें — Parent, Educator, Doctor या Observer।"
                  : "Complete at least one assessment (Parent, Educator, Doctor or Observer) to see cross-stakeholder signals."
              }
            />
          ) : (
            <div style={{ height: 220 }}>
              <Bar data={chartData} options={darkChartOptions()} />
            </div>
          )}
        </div>

        <div className="space-y-3">
          {DOMAINS.map((d) => {
            const ci = consistencyIndex(d.id);
            return (
              <div
                key={d.id}
                className="border border-gray-800 rounded-xl p-4 bg-gray-950"
              >
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-xs font-semibold text-cyan-300 leading-snug flex-1 pr-2">
                    {d.title[lang]}
                  </h3>
                  <div className="text-xs text-right shrink-0">
                    <span className="text-gray-200">Consistency </span>
                    <span
                      className={`font-bold ${
                        ci === "—"
                          ? "text-gray-300"
                          : parseInt(ci) >= 70
                            ? "text-green-400"
                            : "text-yellow-400"
                      }`}
                    >
                      {ci}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {ROLES.map((role, idx) => {
                    const { total, max } = domainStats(role, d);
                    const pct = max ? Math.round((total / max) * 100) : 0;
                    const answered =
                      max > 0 &&
                      Object.keys(responses[role]?.[d.id] || {}).length > 0;
                    return (
                      <div key={role} className="text-center">
                        <p className="text-[9px] uppercase tracking-wider text-gray-200 mb-1">
                          {role}
                        </p>
                        {answered ? (
                          <>
                            <p
                              className="text-sm font-bold"
                              style={{
                                color: roleColors[idx].replace("0.8", "1"),
                              }}
                            >
                              {pct}%
                            </p>
                            <div className="mt-1 h-1 bg-gray-800 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${pct}%`,
                                  backgroundColor: roleColors[idx],
                                }}
                              />
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-gray-300 mt-1">—</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-gray-300 text-center">
          {lang === "hi"
            ? "यह पैटर्न पहचान के लिए है, निदान के लिए नहीं।"
            : "Designed for early pattern recognition, not diagnosis."}
        </p>
      </div>
    </div>
  );
};

export default GLCrossViewDashboard;
